import { useEffect, useState } from "react";
import { Loader2, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getConnectionState,
  installSessionRecovery,
  subscribeToConnection,
  type ConnectionState,
} from "@/lib/session";

/**
 * Tells the user when the app has stopped talking to the server.
 *
 * The original failure mode was entirely silent: the session expired, every
 * request 401'd, and the UI carried on looking healthy — you could keep typing
 * into a document that was no longer saving. Recovery is automatic now, but
 * silence was half the problem, so the state is always visible while it lasts.
 */
export function ConnectionBanner() {
  const [state, setState] = useState<ConnectionState>(getConnectionState);

  useEffect(() => subscribeToConnection(setState), []);

  // Revalidating the session when the tab wakes is what actually prevents the
  // bug, rather than just reporting it — a laptop that slept past the token
  // expiry recovers before the next keystroke fails to save.
  useEffect(() => installSessionRecovery(), []);

  if (state === "connected") return null;

  const signedOut = state === "signed-out";
  const offline = state === "offline";

  return (
    <div
      role="status"
      aria-live="polite"
      className={`connection-banner fixed top-0 inset-x-0 z-[60] flex items-center justify-center gap-2 px-4 py-2 text-sm ${
        signedOut
          ? "bg-destructive text-destructive-foreground"
          : "bg-amber-500/95 text-amber-950"
      }`}
    >
      {signedOut ? (
        <>
          <WifiOff className="h-4 w-4 shrink-0" />
          <span>Your session expired. Sign in again to keep saving.</span>
          <Button
            size="sm"
            variant="secondary"
            className="h-6 ml-1"
            onClick={() => window.location.reload()}
          >
            Sign in
          </Button>
        </>
      ) : offline ? (
        <>
          <WifiOff className="h-4 w-4 shrink-0" />
          <span>Working offline — changes stay on this Mac until you are back online.</span>
        </>
      ) : (
        <>
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          <span>Reconnecting…</span>
        </>
      )}
    </div>
  );
}
